import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { API } from "@/App";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refreshUser, user, authHeaders } = useAuth();
  const [status, setStatus] = useState("checking");
  const [paymentType, setPaymentType] = useState("");

  const checkPaymentStatus = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/payments/status/${sessionId}`,
        { headers: authHeaders() }
      );

      if (response.data.status === "paid") {
        setStatus("success");
        setPaymentType(response.data.payment_type);
        await refreshUser();
        toast.success("Payment successful! 🎉");
      } else {
        setStatus("pending");
      }
    } catch (error) {
      console.error("Payment check error:", error);
      setStatus("error");
    }
  }, [refreshUser, authHeaders]);

  useEffect(() => {
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  return (
    <div className="min-h-screen bg-[#030712] relative">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 via-transparent to-teal-900/5" />
      
      <div className="relative z-10 max-w-2xl mx-auto px-6 py-20">
        {status === "checking" && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#00E0FF] mx-auto mb-6"></div>
            <p className="text-gray-400 text-lg">Verifying payment...</p>
          </div>
        )}

        {status === "success" && (
          <Card className="glass-effect p-12 text-center">
            <div className="w-20 h-20 bg-[#00FFA3]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#00FFA3]" />
            </div>
            
            <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>
              PAYMENT SUCCESSFUL!
            </h1>
            
            <p className="text-gray-400 text-lg mb-8">
              {paymentType === "subscription" 
                ? "You now have unlimited access to AI predictions!"
                : "Your prediction credit has been added to your account!"}
            </p>

            {user && (
              <div className="glass-effect p-6 rounded-lg mb-8">
                <div className="flex items-center justify-center gap-4">
                  <Sparkles className="w-6 h-6 text-[#00E0FF]" />
                  <div className="text-left">
                    <div className="text-sm text-gray-400">Your Status</div>
                    <div className="text-xl font-bold text-white">
                      {user.subscription_status === "subscribed" 
                        ? "Premium Subscriber" 
                        : `${user.prediction_credits} Credits Available`}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Button
              data-testid="go-to-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-bold text-lg px-8 py-6 shadow-[0_0_20px_rgba(0,224,255,0.3)] hover:shadow-[0_0_30px_rgba(0,224,255,0.5)] transition-all duration-300"
            >
              Start Predicting
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Card>
        )}

        {status === "error" && (
          <Card className="glass-effect p-12 text-center">
            <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>
              PAYMENT VERIFICATION FAILED
            </h1>
            <p className="text-gray-400 mb-8">
              We couldn't verify your payment. Please contact support.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5"
            >
              Return to Dashboard
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
